import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import adeleIcon from "../assets/due/pesquisa-de-compliance/adele/icon.png";
import elzaIcon from "../assets/due/pesquisa-de-compliance/elza/icon.png";
import lucasIcon from "../assets/due/pesquisa-de-compliance/lucas/icon.png";
import carlosIcon from "../assets/due/pesquisa-de-compliance/carlos/icon.png";
import srtaIcon from "../assets/due/pesquisa-de-compliance/senhoritaCompliance/icon.png";

const AVATAR_ICONS = {
  adele: adeleIcon,
  elza: elzaIcon,
  lucas: lucasIcon,
  carlos: carlosIcon,
  senhoritacompliance: srtaIcon,
};

const normalizeKey = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .toLowerCase();

const getAvatarIcon = (avatar) => {
  const byId = AVATAR_ICONS[normalizeKey(avatar?.id || "")];
  if (byId) return byId;
  return AVATAR_ICONS[normalizeKey(avatar?.label || "")] || null;
};

export default function InterviewControls({
  features,
  avatars = [],
  selectedAvatar,
  onAvatarChange,
  showCaptions,
  onToggleCaptions,
  videoSpeed,
  onSpeedChange,
  tourId = "step-speed",
}) {
  const multiAvatar = features.avatarPicker && avatars.length > 1;
  const speedOptions = [1, 1.25, 1.5, 2];
  const currentSpeedIndex = Math.max(0, speedOptions.indexOf(videoSpeed));
  const canDecreaseSpeed = currentSpeedIndex > 0;
  const canIncreaseSpeed = currentSpeedIndex < speedOptions.length - 1;

  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const avatarMenuRef = useRef(null);

  const selected = useMemo(() => {
    return avatars.find((avatar) => avatar.id === selectedAvatar) || avatars[0] || null;
  }, [avatars, selectedAvatar]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!avatarMenuRef.current) return;
      if (!avatarMenuRef.current.contains(event.target)) {
        setIsAvatarMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleAvatarSelect = (avatarId) => {
    onAvatarChange?.(avatarId);
    setIsAvatarMenuOpen(false);
  };

  return (
    <TopBar>
      {features.captions && (
        <ControlBox
          type="button"
          as="button"
          data-tour="step-cc"
          onClick={onToggleCaptions}
          $selected={showCaptions}
          aria-pressed={showCaptions}
        >
          CC
        </ControlBox>
      )}
      {features.speed && (
        <SpeedSelector data-tour={tourId}>
          <SpeedLabel>Velocidade</SpeedLabel>
          <SpeedGroup>
            <ArrowButton
              type="button"
              onClick={() => canDecreaseSpeed && onSpeedChange?.(speedOptions[currentSpeedIndex - 1])}
              disabled={!canDecreaseSpeed}
              aria-label="Diminuir velocidade"
            >
              &lt;
            </ArrowButton>
            <SpeedValue>{videoSpeed}x</SpeedValue>
            <ArrowButton
              type="button"
              onClick={() => canIncreaseSpeed && onSpeedChange?.(speedOptions[currentSpeedIndex + 1])}
              disabled={!canIncreaseSpeed}
              aria-label="Aumentar velocidade"
            >
              &gt;
            </ArrowButton>
          </SpeedGroup>
        </SpeedSelector>
      )}
      {multiAvatar && selected && (
        <AvatarDropdown ref={avatarMenuRef} data-tour="step-avatar">
          <AvatarTrigger
            type="button"
            onClick={() => setIsAvatarMenuOpen((prev) => !prev)}
            aria-expanded={isAvatarMenuOpen}
            aria-haspopup="listbox"
          >
            <AvatarThumb src={getAvatarIcon(selected)} alt={selected.label} />
            <AvatarChevron>{isAvatarMenuOpen ? "▴" : "▾"}</AvatarChevron>
          </AvatarTrigger>
          {isAvatarMenuOpen && (
            <AvatarMenu role="listbox" aria-label="Selecionar avatar">
              {avatars.map((avatar) => (
                <AvatarOption
                  key={avatar.id}
                  type="button"
                  role="option"
                  aria-selected={selectedAvatar === avatar.id}
                  $selected={selectedAvatar === avatar.id}
                  onClick={() => handleAvatarSelect(avatar.id)}
                >
                  <AvatarThumb src={getAvatarIcon(avatar)} alt={avatar.label} />
                  <AvatarLabel>{avatar.label}</AvatarLabel>
                </AvatarOption>
              ))}
            </AvatarMenu>
          )}
        </AvatarDropdown>
      )}
    </TopBar>
  );
}

const TopBar = styled.div`
  display: contents;
`;

const ControlBox = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  background: ${({ $selected }) => ($selected ? "rgba(0, 0, 0, 0.75)" : "rgba(0, 0, 0, 0.5)")};
  border-radius: 10px;
  border: none;
  color: #fff;
  font-size: 14px;
  font-family: "Poppins", sans-serif;
  cursor: pointer;
  box-sizing: border-box;
  pointer-events: auto;
`;

const SpeedSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  margin: 0;
  width: fit-content;
  height: 44px;
  box-sizing: border-box;
  position: relative;
  z-index: 4;
  pointer-events: auto;
`;

const SpeedGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: auto;
`;

const SpeedLabel = styled.span`
  color: #fff;
  font-size: 12px;
  font-family: "Poppins", sans-serif;
  margin-right: 4px;
`;

const ArrowButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: background-color 0.3s ease, transform 0.2s;
  pointer-events: auto;

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.35)"};
    transform: ${({ disabled }) => (disabled ? "none" : "scale(1.05)")};
  }
`;

const SpeedValue = styled.span`
  color: #fff;
  font-size: 12px;
  font-family: "Poppins", sans-serif;
  min-width: 44px;
  text-align: center;
`;

const AvatarDropdown = styled.div`
  position: relative;
  pointer-events: auto;
  z-index: 5;
`;

const AvatarTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 44px;
  height: 44px;
  padding: 0 8px;
  border: none;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
`;

const AvatarMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  max-height: 220px;
  overflow-y: auto;
  background: rgba(10, 10, 10, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
`;

const AvatarOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? "rgba(40, 167, 69, 0.35)" : "transparent")};
  color: #fff;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const AvatarThumb = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

const AvatarLabel = styled.span`
  color: #fff;
  font-size: 12px;
  font-family: "Poppins", sans-serif;
  white-space: nowrap;
`;

const AvatarChevron = styled.span`
  margin-left: auto;
  color: #fff;
  font-size: 11px;
`;




